import React from 'react';
import { motion } from 'framer-motion';

const ReliabotixLogo = ({ 
  size = 64, 
  variant = 'full', // 'full', 'minimal', 'icon', 'text'
  animate = true,
  className = ''
}) => {
  const logoVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const elementVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: { duration: 0.8, ease: "easeInOut" }
    }
  };

  const pulseVariants = {
    initial: { scale: 1 },
    animate: {
      scale: [1, 1.02, 1],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const LogoComponent = () => (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 120 120" 
      className={className}
    >
      <defs>
        {/* Clean background gradient */}
        <linearGradient id="backgroundGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>
        
        {/* Tech accent gradient */}
        <linearGradient id="techGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0EA5E9" />
          <stop offset="50%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#1D4ED8" />
        </linearGradient>
        
        {/* Shadow filter */}
        <filter id="dropShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="2" dy="4" stdDeviation="4" floodColor="#000000" floodOpacity="0.3"/>
        </filter>

        <filter id="techGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Main Logo Container */}
      <motion.g
        variants={elementVariants}
        initial="initial"
        animate={animate ? "animate" : "initial"}
      >
        {/* Clean Circular Background */}
        <motion.circle
          cx="60"
          cy="60"
          r="50"
          fill="url(#backgroundGradient)"
          stroke="none"
          filter="url(#dropShadow)"
          variants={animate ? pulseVariants : {}}
          initial="initial"
          animate={animate ? "animate" : "initial"}
        />

        {/* White RB Letters */}
        <motion.g
          variants={elementVariants}
          initial="initial"
          animate={animate ? "animate" : "initial"}
          transform="translate(60, 60)"
        >
          {/* Letter R */}
          <motion.path
            d="M -22 -20 
               L -22 20
               M -22 -20
               L -8 -20
               C -2 -20, 2 -16, 2 -10
               C 2 -4, -2 0, -8 0
               L -22 0
               M -8 0
               L 2 20"
            stroke="#FFFFFF"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />

          {/* Letter B */}
          <motion.path
            d="M 8 -20
               L 8 20
               M 8 -20
               L 18 -20
               C 22 -20, 24 -18, 24 -14
               C 24 -12, 22 -10, 20 -10
               L 8 -10
               M 8 -10
               L 20 -10
               C 24 -10, 26 -8, 26 -4
               C 26 0, 24 2, 20 2
               L 8 2"
            stroke="#FFFFFF"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </motion.g>

        {/* Modern Tech Accents - Small dots */}
        <motion.g>
          {/* Tech indicator dots */}
          <motion.circle
            cx="95"
            cy="35"
            r="3"
            fill="url(#techGradient)"
            filter="url(#techGlow)"
            animate={animate ? {
              scale: [1, 1.2, 1],
              opacity: [0.8, 1, 0.8]
            } : {}}
            transition={{
              duration: 2,
              delay: 0.2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          <motion.circle
            cx="95"
            cy="50"
            r="2.5"
            fill="#8B5CF6"
            filter="url(#techGlow)"
            animate={animate ? {
              scale: [1, 1.15, 1],
              opacity: [0.7, 1, 0.7]
            } : {}}
            transition={{
              duration: 2.2,
              delay: 0.4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          <motion.circle
            cx="95"
            cy="65"
            r="2"
            fill="#06B6D4"
            filter="url(#techGlow)"
            animate={animate ? {
              scale: [1, 1.1, 1],
              opacity: [0.6, 1, 0.6]
            } : {}}
            transition={{
              duration: 2.5,
              delay: 0.6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.g>
      </motion.g>

      {/* Company Name Text (for full variant) */}
      {(variant === 'full' || variant === 'text') && (
        <motion.g
          variants={elementVariants}
          initial="initial"
          animate={animate ? "animate" : "initial"}
        >
          <motion.text
            x="60"
            y="108"
            textAnchor="middle"
            className="text-xs font-bold tracking-wide"
            fill="#FFFFFF"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            RELIABOTIX
          </motion.text>
        </motion.g>
      )}
    </svg>
  );

  if (animate) {
    return (
      <motion.div
        variants={logoVariants}
        initial="initial"
        animate="animate"
        whileHover={{ 
          scale: 1.05,
          transition: { duration: 0.2 }
        }}
        className="inline-block"
      >
        <LogoComponent />
      </motion.div>
    );
  }

  return <LogoComponent />;
};

export default ReliabotixLogo;